import React, { useState } from "react";
import { Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";

import * as SubframeCore from "@subframe/core";
import { Button } from "subframe/components/Button";
import { IconButton, TextArea, ToggleGroup } from "subframe";

import { isEmpty as isStringEmpty } from "utils/string";

import { EVENT_TYPES } from "constants/mixpanel";
import { mixpanelTrackEvent } from "services/mixpanel";

import {
  RECOGNITION_CATEGORY,
  RECOGNITION_DIRECTED_AT,
} from "modules/Retention/constants/constants";
import { postRecognition } from "modules/Retention/services/retention";

function SendRecognition({
  employeeId,
  flat = false,
  focused = false,
  closeButtonIcon,
  canMinimize = false,
  isMinimized = false,
  onSubmit = () => {},
  onMinimize = () => {},
}) {
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState(
    RECOGNITION_CATEGORY.CLINICAL_EXCELLENCE.value
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);
  const [isSuccessMessageShown, setIsSuccessMessageShown] = useState(false);
  const [hasTriedFormSubmission, setHasTriedFormSubmission] = useState(false);

  const isFormValid = !isStringEmpty(category) && !isStringEmpty(reason);

  const resetForm = () => {
    setCategory(RECOGNITION_CATEGORY.CLINICAL_EXCELLENCE.value);
    setReason("");
    setHasTriedFormSubmission(false);
  };

  /**
   * Submits the recognition post form.
   *
   * @param {Object} payload
   * @returns {Object}
   */
  const submitRecognitionPostAsync = async (payload) => {
    try {
      return await postRecognition(payload);
    } catch (error) {
      return { hasError: true, error };
    }
  };

  /**
   * Handles the submission of the new recognition.
   */
  const handleFormSubmit = async () => {
    setHasTriedFormSubmission(true);

    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);

    const response = await submitRecognitionPostAsync({
      recognized_id: employeeId,
      directed_at: RECOGNITION_DIRECTED_AT.DIRECT_REPORT.value, // hardcoded this option for now
      category,
      reason,
    });

    setIsSubmitting(false);

    mixpanelTrackEvent(EVENT_TYPES.RECOGNITION_SEND);

    if (response?.hasError) {
      setErrorMessage(`Error while submitting: ${response?.error?.message}`);
      setIsErrorMessageShown(true);

      return;
    }

    resetForm();
    setSuccessMessage("Successfully sent recognition.");
    setIsSuccessMessageShown(true);
    onSubmit();
    onMinimize();
  };

  /**
   * Renders the error snackbar.
   *
   * @returns {Component}
   */
  const renderErrorSnackbar = () => (
    <Snackbar
      open={isErrorMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsErrorMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsErrorMessageShown(false)}
        severity="error"
        sx={{ width: "100%" }}
      >
        {errorMessage}
      </MuiAlert>
    </Snackbar>
  );

  /**
   * Renders the success snackbar.
   *
   * @returns {Component}
   */
  const renderSuccessSnackbar = () => (
    <Snackbar
      open={isSuccessMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsSuccessMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsSuccessMessageShown(false)}
        severity="success"
        sx={{ width: "100%" }}
      >
        {successMessage}
      </MuiAlert>
    </Snackbar>
  );

  return (
    <>
      {!isMinimized && (
        <div
          className={SubframeCore.twClassNames(
            "w-full border-2 border-solid border-neutral-border rounded flex-col shadow items-start flex bg-default-background group/a7126f05 sticky bottom-0",
            {
              "border-2 border-solid border-default-background shadow-none":
                flat,
              "border-2 border-solid border-brand-primary": focused,
            }
          )}
        >
          <div className="w-full gap-2 pt-3 pr-3 pl-5 items-center flex">
            <div className="grow shrink-0 basis-0 w-full gap-2 items-center flex">
              <SubframeCore.Icon
                className="text-brand-600 text-body font-body"
                name="FeatherAward"
              />
              <span className="text-default-font text-body-bold font-body-bold">
                Recognition
              </span>
            </div>
            {canMinimize && (
              <IconButton
                icon={
                  !isStringEmpty(closeButtonIcon)
                    ? closeButtonIcon
                    : "FeatherMinimize2"
                }
                size="small"
                title="Minimize"
                onClick={onMinimize}
              />
            )}
          </div>
          <div className="w-full flex-col gap-2 pt-4 pr-4 pb-4 pl-4 items-start flex">
            <ToggleGroup
              className="flex-none h-auto w-full"
              value={category}
              onValueChange={setCategory}
            >
              <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-px">
                <ToggleGroup.Item
                  className="h-10 w-full flex-none"
                  icon="FeatherCheck"
                  value={RECOGNITION_CATEGORY.CLINICAL_EXCELLENCE.value}
                >
                  {RECOGNITION_CATEGORY.CLINICAL_EXCELLENCE.label}
                </ToggleGroup.Item>
                <ToggleGroup.Item
                  className="h-10 w-full flex-none"
                  icon="FeatherUserPlus"
                  value={RECOGNITION_CATEGORY.PATIENT_SATISFACTION.value}
                >
                  {RECOGNITION_CATEGORY.PATIENT_SATISFACTION.label}
                </ToggleGroup.Item>
                <ToggleGroup.Item
                  className="h-10 w-full flex-none"
                  icon="FeatherMic2"
                  value={RECOGNITION_CATEGORY.COMMUNICATION.value}
                >
                  {RECOGNITION_CATEGORY.COMMUNICATION.label}
                </ToggleGroup.Item>
              </div>
              <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-px">
                <ToggleGroup.Item
                  className="h-10 w-full flex-none"
                  icon="FeatherLifeBuoy"
                  value={RECOGNITION_CATEGORY.TEAMWORK.value}
                >
                  {RECOGNITION_CATEGORY.TEAMWORK.label}
                </ToggleGroup.Item>
                <ToggleGroup.Item
                  className="h-10 w-full flex-none"
                  icon="FeatherSmilePlus"
                  value={RECOGNITION_CATEGORY.PERSONAL_IMPROVEMENT.value}
                >
                  {RECOGNITION_CATEGORY.PERSONAL_IMPROVEMENT.label}
                </ToggleGroup.Item>
                <ToggleGroup.Item
                  className="h-10 w-full flex-none"
                  icon="FeatherRocket"
                  value={RECOGNITION_CATEGORY.OTHER.value}
                >
                  {RECOGNITION_CATEGORY.OTHER.label}
                </ToggleGroup.Item>
              </div>
            </ToggleGroup>

            <TextArea
              onChange={(event) => setReason(event?.target?.value)}
              className="flex-none h-auto w-full"
              label=""
              helpText={
                hasTriedFormSubmission && isStringEmpty(reason)
                  ? "Reason is required"
                  : ""
              }
              error={hasTriedFormSubmission && isStringEmpty(reason)}
            >
              <TextArea.Input
                value={reason}
                placeholder="Add recognition reason..."
              />
            </TextArea>

            <div className="w-full gap-2 justify-end flex">
              <Button
                disabled={isSubmitting}
                loading={isSubmitting}
                onClick={handleFormSubmit}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      )}
      {renderErrorSnackbar()}
      {renderSuccessSnackbar()}
    </>
  );
}

export default SendRecognition;
